import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { api, fmtUSD } from '@/lib/api';
import { SecurityBanner } from '@/components/SecurityBanner';
import { PaymentMethodPicker } from '@/components/PaymentMethodPicker';

export default function PaymentPage() {
  const { quoteId } = useParams();
  const [q, setQ] = useState(null);
  const [method, setMethod] = useState('airtel_money');
  const [paying, setPaying] = useState(false);
  const nav = useNavigate();

  useEffect(() => { api.get(`/quotes/${quoteId}`).then((r) => setQ(r.data)); }, [quoteId]);

  const pay = async () => {
    setPaying(true);
    try {
      await api.post('/payments/initiate', { quote_id: quoteId, payment_method: method });
      toast.success('Paiement effectué — fonds en séquestre');
      nav(`/quote/${quoteId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de paiement');
    } finally { setPaying(false); }
  };

  if (!q) return <div className="p-6">Chargement…</div>;
  const b = q.breakdown;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 space-y-4" data-testid="payment-page">
      <h1 className="text-2xl font-bold">Paiement sécurisé</h1>
      <SecurityBanner />

      <Card className="p-4">
        <p className="text-sm text-slate-600">Devis</p>
        <p className="font-semibold">{q.title}</p>
        <Separator className="my-3" />
        <div className="space-y-1 text-sm">
          <Row label="Main d'œuvre" value={fmtUSD(b.labor_cents)} />
          <Row label="Frais annexes" value={fmtUSD(b.expenses_cents)} />
          <Row label="Commission 10% (MO)" value={fmtUSD(b.fee_mo_total)} muted />
          <Row label="Assurance 3% (FA)" value={fmtUSD(b.fee_fa_insurance)} muted />
          <Separator className="my-2" />
          <Row label="Total à payer" value={fmtUSD(b.client_total)} strong testid="payment-total" />
        </div>
      </Card>

      <Card className="p-4">
        <h3 className="font-semibold mb-3">Choisissez votre moyen de paiement</h3>
        <PaymentMethodPicker value={method} onChange={setMethod} />
      </Card>

      <Card className="p-4 bg-[#DFF7EA] border-[#1F9D63]/30">
        <div className="flex items-start gap-3">
          <Shield size={20} className="text-[#16764A] mt-0.5" />
          <div className="text-sm text-[#16764A]">
            <p className="font-semibold">Vos fonds sont sécurisés</p>
            <p>Ils restent en séquestre chez Job Kongo jusqu&apos;à la fin de la prestation. En cas de problème, vous pouvez ouvrir un litige.</p>
          </div>
        </div>
      </Card>

      <Button onClick={pay} disabled={paying} size="lg" className="w-full bg-[#1677F2] hover:bg-[#0F5FCC]" data-testid="payment-confirm-button">
        <CheckCircle2 size={16} className="mr-2" /> Confirmer le paiement de {fmtUSD(b.client_total)}
      </Button>
      <p className="text-[11px] text-slate-500 text-center">V1 : paiement simulé pour démonstration. Les vraies intégrations (Airtel, Orange, M-Pesa…) seront branchées en Phase 2.</p>
    </div>
  );
}

const Row = ({ label, value, muted, strong, testid }) => (
  <div className={`flex items-center justify-between ${strong ? 'text-base' : ''}`}>
    <span className={muted ? 'text-slate-600' : 'text-slate-700'}>{label}</span>
    <span className={`font-mono-num ${strong ? 'font-bold text-[#0B1220]' : 'font-semibold text-[#0B1220]'}`} data-testid={testid}>{value}</span>
  </div>
);
